/**
 * Training Module Detail Page
 * GitHub Issue #285
 *
 * View and edit training module content. Also serves as the training viewer
 * for users completing assignments.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  api,
  TrainingModule,
  TrainingProgress,
  TrainingQuizQuestion,
  TrainingQuizSubmission,
  TrainingContentType,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useAuth } from '../hooks/useAuth'
import {
  ArrowLeft,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  AlertCircle,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  Edit2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Award,
  Timer,
} from 'lucide-react'

// Content type icons
const contentTypeIcons: Record<TrainingContentType, any> = {
  video: Video,
  slides: FileText,
  quiz: HelpCircle,
  document: FileText,
  mixed: BookOpen,
}

export default function TrainingModuleDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  // State
  const [loading, setLoading] = useState(true)
  const [module, setModule] = useState<TrainingModule | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [progress, setProgress] = useState<TrainingProgress | null>(null)
  const [assignmentId, setAssignmentId] = useState<string | null>(null)

  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false)
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string | string[]>>({})
  const [quizSubmitting, setQuizSubmitting] = useState(false)
  const [quizResults, setQuizResults] = useState<{
    score: number
    passed: boolean
    passing_score: number
    correct_answers: number
    total_questions: number
    feedback: Array<{
      question_id: string
      correct: boolean
      correct_answer: string | string[]
      explanation: string | null
    }>
  } | null>(null)

  // Video state
  const [videoPlaying, setVideoPlaying] = useState(false)

  // Slides state
  const [currentSlide, setCurrentSlide] = useState(0)

  // Edit form
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    video_url: '',
    estimated_minutes: 30,
    quiz_passing_score: 70,
  })

  // Fetch module
  const fetchModule = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const response = await api.getTrainingModule(id)
      if (response.success) {
        setModule(response.data)
        setEditForm({
          title: response.data.title,
          description: response.data.description || '',
          video_url: response.data.video_url || '',
          estimated_minutes: response.data.estimated_minutes,
          quiz_passing_score: response.data.quiz_passing_score,
        })
      }
    } catch (error) {
      console.error('Failed to fetch module:', error)
      toast.error('Failed to load training module')
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => {
    fetchModule()
  }, [fetchModule])

  // Save module edits
  const handleSave = async () => {
    if (!module) return
    try {
      const response = await api.updateTrainingModule(module.id, editForm)
      if (response.success) {
        toast.success('Module updated successfully')
        setModule(response.data)
        setIsEditing(false)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update module')
    }
  }

  // Submit quiz
  const handleSubmitQuiz = async () => {
    if (!module || !assignmentId) return

    const questions = module.quiz_questions as TrainingQuizQuestion[]
    const answers: TrainingQuizSubmission[] = questions.map(q => ({
      question_id: q.id,
      answer: quizAnswers[q.id] || '',
    }))

    setQuizSubmitting(true)
    try {
      const response = await api.submitTrainingQuiz(assignmentId, module.id, answers)
      if (response.success) {
        setQuizResults(response.results)
        setProgress(response.data)
        if (response.results.passed) {
          toast.success('Congratulations! You passed the quiz.')
        } else {
          toast.warning('You did not pass. Please review and try again.')
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit quiz')
    } finally {
      setQuizSubmitting(false)
    }
  }

  // Mark module as complete (for non-quiz modules)
  const handleMarkComplete = async () => {
    if (!module || !assignmentId) return
    try {
      const response = await api.completeTrainingModule(assignmentId, module.id)
      if (response.success) {
        toast.success('Module completed!')
        setProgress(response.data)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete module')
    }
  }

  // Retry quiz
  const handleRetryQuiz = () => {
    setQuizAnswers({})
    setQuizResults(null)
    setQuizStarted(true)
  }

  // Get YouTube embed URL
  const getVideoEmbedUrl = (url: string | null) => {
    if (!url) return null

    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`
    }

    // Direct URL
    return url
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Module not found</h2>
        <p className="text-gray-500 mb-4">The training module you're looking for doesn't exist.</p>
        <Link to="/training" className="btn btn-primary">
          Back to Training
        </Link>
      </div>
    )
  }

  const TypeIcon = contentTypeIcons[module.content_type]
  const embedUrl = getVideoEmbedUrl(module.video_url)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <TypeIcon className="h-6 w-6 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="input text-xl font-bold"
                />
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">{module.title}</h1>
              )}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                module.status === 'published' ? 'bg-green-100 text-green-700' :
                module.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {module.status}
              </span>
            </div>
            {isEditing ? (
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="input w-full mt-2"
                rows={2}
                placeholder="Module description..."
              />
            ) : (
              module.description && (
                <p className="text-gray-500 mt-1">{module.description}</p>
              )
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {user?.role === 'admin' && (
            isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false)
                    setEditForm({
                      title: module.title,
                      description: module.description || '',
                      video_url: module.video_url || '',
                      estimated_minutes: module.estimated_minutes,
                      quiz_passing_score: module.quiz_passing_score,
                    })
                  }}
                  className="btn btn-secondary"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-secondary"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </button>
            )
          )}
        </div>
      </div>

      {/* Module Info */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {isEditing ? (
              <input
                type="number"
                value={editForm.estimated_minutes}
                onChange={(e) => setEditForm({ ...editForm, estimated_minutes: parseInt(e.target.value) || 0 })}
                className="input w-20"
                min="1"
              />
            ) : (
              `${module.estimated_minutes} minutes`
            )}
          </span>
          {module.category_name && (
            <span
              className="inline-flex items-center px-2 py-1 rounded-full text-xs"
              style={{
                backgroundColor: `${module.category_color || '#6b7280'}20`,
                color: module.category_color || '#6b7280',
              }}
            >
              {module.category_name}
            </span>
          )}
          {module.is_mandatory && (
            <span className="text-red-600 font-medium">Mandatory</span>
          )}
          {module.content_type === 'quiz' || module.content_type === 'mixed' ? (
            <span className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Passing score: {isEditing ? (
                <input
                  type="number"
                  value={editForm.quiz_passing_score}
                  onChange={(e) => setEditForm({ ...editForm, quiz_passing_score: parseInt(e.target.value) || 70 })}
                  className="input w-16"
                  min="0"
                  max="100"
                />
              ) : (
                `${module.quiz_passing_score}%`
              )}
            </span>
          ) : null}
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {/* Video Content */}
        {(module.content_type === 'video' || module.content_type === 'mixed') && embedUrl && (
          <div className="aspect-video bg-black">
            {isEditing ? (
              <div className="p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                <input
                  type="url"
                  value={editForm.video_url}
                  onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })}
                  className="input w-full"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            ) : (
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            )}
          </div>
        )}

        {/* Document Content */}
        {module.content_type === 'document' && module.document_content && (
          <div className="p-6 prose max-w-none">
            <div dangerouslySetInnerHTML={{ __html: module.document_content }} />
          </div>
        )}

        {/* Slides Content */}
        {module.content_type === 'slides' && module.slides_content && (
          <div>
            <div className="p-6 min-h-[400px]">
              {/* Slide content would render here */}
              <div className="text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Slide viewer coming soon</p>
              </div>
            </div>
            <div className="border-t p-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Slide {currentSlide + 1} of {(module.slides_content as any[])?.length || 1}
              </span>
              <button
                onClick={() => setCurrentSlide(currentSlide + 1)}
                className="btn btn-secondary"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Quiz Content */}
        {(module.content_type === 'quiz' || module.content_type === 'mixed') && module.quiz_questions && (
          <div className="p-6">
            {!quizStarted && !quizResults ? (
              <div className="text-center py-8">
                <HelpCircle className="h-16 w-16 text-primary-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to take the quiz?</h3>
                <p className="text-gray-600 mb-4">
                  This quiz has {(module.quiz_questions as TrainingQuizQuestion[]).length} questions.
                  You need {module.quiz_passing_score}% to pass.
                </p>
                {module.quiz_time_limit_minutes && (
                  <p className="text-sm text-gray-500 mb-4">
                    <Timer className="h-4 w-4 inline mr-1" />
                    Time limit: {module.quiz_time_limit_minutes} minutes
                  </p>
                )}
                <button
                  onClick={() => setQuizStarted(true)}
                  className="btn btn-primary"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Quiz
                </button>
              </div>
            ) : quizResults ? (
              <div className="space-y-6">
                {/* Results Summary */}
                <div className={`text-center py-8 rounded-lg ${quizResults.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                  {quizResults.passed ? (
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  ) : (
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                  )}
                  <h3 className={`text-2xl font-bold mb-2 ${quizResults.passed ? 'text-green-700' : 'text-red-700'}`}>
                    {quizResults.passed ? 'Congratulations!' : 'Not Quite...'}
                  </h3>
                  <p className="text-lg text-gray-700 mb-2">
                    Your score: <span className="font-bold">{quizResults.score}%</span>
                  </p>
                  <p className="text-gray-600">
                    {quizResults.correct_answers} of {quizResults.total_questions} correct
                    (Passing: {quizResults.passing_score}%)
                  </p>
                </div>

                {/* Feedback */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Review Your Answers</h4>
                  {(module.quiz_questions as TrainingQuizQuestion[]).map((question, idx) => {
                    const feedback = quizResults.feedback.find(f => f.question_id === question.id)
                    const userAnswer = quizAnswers[question.id]
                    return (
                      <div key={question.id} className={`p-4 rounded-lg border ${feedback?.correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-start gap-3">
                          {feedback?.correct ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-red-500 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-2">
                              {idx + 1}. {question.question}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              Your answer: <span className="font-medium">{Array.isArray(userAnswer) ? userAnswer.join(', ') : userAnswer}</span>
                            </p>
                            {!feedback?.correct && (
                              <p className="text-sm text-green-700">
                                Correct answer: <span className="font-medium">{Array.isArray(feedback?.correct_answer) ? feedback?.correct_answer.join(', ') : feedback?.correct_answer}</span>
                              </p>
                            )}
                            {feedback?.explanation && (
                              <p className="text-sm text-gray-500 mt-2 italic">{feedback.explanation}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {!quizResults.passed && (
                  <div className="text-center">
                    <button
                      onClick={handleRetryQuiz}
                      className="btn btn-primary"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {(module.quiz_questions as TrainingQuizQuestion[]).map((question, idx) => (
                  <div key={question.id} className="p-4 border rounded-lg">
                    <p className="font-medium text-gray-900 mb-3">
                      {idx + 1}. {question.question}
                    </p>
                    {question.question_type === 'true_false' ? (
                      <div className="space-y-2">
                        {['True', 'False'].map((option) => (
                          <label
                            key={option}
                            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name={question.id}
                              value={option}
                              checked={quizAnswers[question.id] === option}
                              onChange={(e) => setQuizAnswers({ ...quizAnswers, [question.id]: e.target.value })}
                              className="text-primary-600 focus:ring-primary-500"
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : question.question_type === 'multi_select' ? (
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={Array.isArray(quizAnswers[question.id]) && (quizAnswers[question.id] as string[]).includes(option)}
                              onChange={(e) => {
                                const current = (quizAnswers[question.id] as string[]) || []
                                if (e.target.checked) {
                                  setQuizAnswers({ ...quizAnswers, [question.id]: [...current, option] })
                                } else {
                                  setQuizAnswers({ ...quizAnswers, [question.id]: current.filter(o => o !== option) })
                                }
                              }}
                              className="rounded text-primary-600 focus:ring-primary-500"
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name={question.id}
                              value={option}
                              checked={quizAnswers[question.id] === option}
                              onChange={(e) => setQuizAnswers({ ...quizAnswers, [question.id]: e.target.value })}
                              className="text-primary-600 focus:ring-primary-500"
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex items-center justify-between pt-4 border-t">
                  <button
                    onClick={() => {
                      setQuizStarted(false)
                      setQuizAnswers({})
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitQuiz}
                    disabled={quizSubmitting || Object.keys(quizAnswers).length < (module.quiz_questions as TrainingQuizQuestion[]).length}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {quizSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Submit Quiz
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No content */}
        {module.content_type === 'video' && !module.video_url && !isEditing && (
          <div className="p-12 text-center">
            <Video className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No video content has been added yet.</p>
          </div>
        )}
      </div>

      {/* Completion Actions */}
      {progress?.status !== 'completed' && module.content_type !== 'quiz' && assignmentId && (
        <div className="flex justify-end">
          <button
            onClick={handleMarkComplete}
            className="btn btn-primary"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Mark as Complete
          </button>
        </div>
      )}
    </div>
  )
}
